import type React from "react";

import { faChartMixed } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useGetTherapistPatientsFunctionality } from "api/services/therapist/hooks";
import { NoGraphData } from "routes/admin/components/NoGraphData";
import { calculatePercentageChange } from "routes/dashboard/default/utils/calculateChange";
import { ChartToolbar } from "routes/patients/PatientProfile/components/Overview/components/ChartToolbar/ChartToolbar";
import { LineChart } from "shared/atoms/Charts";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout";
import { WidgetBox } from "shared/containers/WidgetBox";

export const PatientsFunctionality: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: patientsFunctionality, isLoading } = useGetTherapistPatientsFunctionality();

  const data = patientsFunctionality?.items || [];
  const hasData = data?.length > 0;
  const sinceStart = calculatePercentageChange(data, "avg_functionality");

  const getSinceStartLabel = () => {
    if (!sinceStart || sinceStart === "0%") return "";
    if (sinceStart.startsWith("+")) return t("dashboard.patients_average_pain.increased");
    return t("dashboard.patients_average_pain.decreased");
  };

  return (
    <WidgetBox
      title={t("dashboard.patients_average_functionality.title")}
      description={t("dashboard.patients_average_functionality.description")}
      icon={<FontAwesomeIcon icon={faChartMixed} />}
      color="yellow"
      toolbar={
        <ChartToolbar
          showDivider={false}
          sinceStart={sinceStart}
          chartType="physical-function"
          hasData={hasData}
          sinceStartLabel={getSinceStartLabel()}
        />
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton />}
        {!isLoading && !hasData && <NoGraphData title={t("patients.graph.no_data")} />}
        {!isLoading && hasData && (
          <LineChart
            data={data}
            height={240}
            lines={[
              {
                dataKey: "avg_functionality",
                name: t("dashboard.patients_average_functionality.line_name"),
                color: theme.colors.redesign.y100,
              },
            ]}
            tooltipProps={{
              color: theme.colors.redesign.y100,
              labelFormatter: (label: string) => t("common.week_num", { number: label }),
            }}
            xAxisProps={{
              dataKey: "week_number",
            }}
            yAxisProps={{
              dataKey: "avg_functionality",
            }}
          />
        )}
      </Box>
    </WidgetBox>
  );
};
